import { useEffect, useRef } from 'react';

import isEqual from 'lodash/isEqual';

const DEFAULT_INTERVAL = 500;

/**
 * Compara os filtros do formulário com os filtros já aplicados na listagem.
 *
 * Usa o isEqual do lodash porque o areObjectsEqual do projeto considera dois objetos Date
 * sempre iguais, o que faria a troca de um filtro de data passar despercebida.
 */
export const areFiltersEqual = (filters, otherFilters) => {
    return isEqual(filters || {}, otherFilters || {});
};

/**
 * Submete o formulário de filtros sempre que um filtro é adicionado ou retirado,
 * sem depender do clique no botão de busca.
 *
 * O submit é adiado por `interval` para não disparar uma requisição a cada tecla
 * digitada, e só acontece quando os valores do formulário estão diferentes dos
 * filtros da última busca enviada.
 *
 * @param {object} form instância do Final Form
 * @param {object} appliedFilters filtros aplicados na última busca
 * @param {number} interval tempo de espera, em ms, antes de submeter
 */
const useAutoSubmitFilters = (form, appliedFilters, interval = DEFAULT_INTERVAL) => {
    const appliedFiltersRef = useRef(appliedFilters);
    appliedFiltersRef.current = appliedFilters;

    useEffect(() => {
        let timeout = null;
        let wasSubmitting = false;
        let lastSubmittedValues = null;

        const scheduleSubmit = () => {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
                const { values, submitting } = form.getState();

                /*
                 * Confere de novo antes de submeter porque os filtros aplicados podem ter
                 * mudado enquanto o intervalo corria, como acontece na carga inicial da
                 * listagem, quando o formulário é inicializado com os filtros salvos.
                 */
                if (areFiltersEqual(values, appliedFiltersRef.current)) return;

                /*
                 * O Final Form descarta um submit enquanto outro está em andamento, então
                 * a troca de filtro fica pendente aqui e é reagendada quando a busca atual
                 * terminar. Sem isso o filtro digitado durante uma busca era perdido e a
                 * listagem continuava mostrando o resultado do filtro anterior.
                 */
                if (submitting) return;

                lastSubmittedValues = { ...values };
                form.submit();
            }, interval);
        };

        const unsubscribe = form.subscribe(({ values, submitting }) => {
            const hasFinishedSubmitting = wasSubmitting && !submitting;
            wasSubmitting = submitting;

            if (areFiltersEqual(values, appliedFiltersRef.current)) return;

            /*
             * Ao final de uma busca só reagenda se o filtro mudou depois do último envio,
             * para não repetir indefinidamente a mesma busca.
             */
            if (hasFinishedSubmitting && areFiltersEqual(values, lastSubmittedValues)) return;

            scheduleSubmit();
        }, { values: true, submitting: true });

        return () => {
            clearTimeout(timeout);
            unsubscribe();
        };
    }, [form, interval]);
};

export default useAutoSubmitFilters;
